import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, dollars } from "@/lib/api";
import TrackList from "@/components/TrackList";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ShoppingCart, ShoppingBag, Package } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import TipJarModal from "@/components/TipJarModal";
import ShareButtons from "@/components/ShareButtons";

export default function AlbumDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get(`/albums/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return <div className="p-16 text-center font-mono text-xs uppercase text-gray-500">Loading...</div>;
  const { album, tracks } = data;

  const buy = async () => {
    if (!user) { toast.error("Sign in to buy"); nav("/login"); return; }
    try {
      const { data: d } = await api.post("/payments/checkout", {
        kind: "album", item_id: album.id, origin_url: window.location.origin,
      });
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(errMsg(e)); }
  };

  const buyBundle = async () => {
    if (!user) { toast.error("Sign in to buy"); nav("/login"); return; }
    try {
      const { data: d } = await api.post("/payments/checkout", {
        kind: "bundle", item_id: album.id, origin_url: window.location.origin,
      });
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="pb-32">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        <div className="grid md:grid-cols-[300px_1fr] gap-8 mb-10">
          <div className={`relative aspect-square bg-neutral-800 border overflow-hidden ${album.is_free ? "border-[#FF4500] shadow-[0_0_60px_-15px_rgba(255,69,0,0.7)]" : "border-[#2A2A2A]"}`}>
            {album.cover_path && <img src={fileUrl(album.cover_path)} alt={album.title} className="h-full w-full object-cover" />}
            {album.is_free && (
              <>
                <div className="absolute -top-3 -right-3 rotate-12">
                  <div className="bg-[#FF4500] text-black font-display font-black text-3xl tracking-tight px-5 py-2 shadow-2xl">
                    FREE
                  </div>
                </div>
                <div className="absolute inset-0 border-4 border-[#FF4500] pointer-events-none animate-pulse" />
              </>
            )}
          </div>
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2">Album{album.is_free && " · FREE DOWNLOAD"}</div>
            <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">{album.title}</h1>
            <Link to={`/artist/${album.artist_id}`} className="font-mono text-sm text-gray-400 hover:text-[#FF4500] mt-2 inline-block">by {album.artist_name}</Link>
            {album.description && <p className="mt-4 text-gray-300 max-w-2xl">{album.description}</p>}
            {album.is_free && (
              <div data-testid="album-free-banner" className="mt-5 inline-flex items-center gap-3 border-2 border-[#FF4500] bg-[#FF4500]/10 px-4 py-2">
                <span className="font-display font-black text-3xl md:text-4xl text-[#FF4500] tracking-tight">FREE ALBUM</span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-gray-300">Every track — no card required</span>
              </div>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              {album.is_free ? (
                <div className="font-mono text-xs uppercase tracking-widest text-[#FF4500] flex items-center gap-2 border border-[#FF4500] bg-[#FF4500]/10 px-4 h-11">
                  <ShoppingCart className="h-4 w-4" /> Free download — tap any track below
                </div>
              ) : (
                <>
                  <Button data-testid="buy-album-btn" onClick={buy} className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono uppercase tracking-widest h-11 px-6">
                    <ShoppingCart className="h-4 w-4 mr-2" /> Buy album {dollars(album.price_cents || 1500)}
                  </Button>
                  <Button data-testid="buy-bundle-btn" onClick={buyBundle} variant="outline"
                          className="rounded-none border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500]/10 bg-transparent font-mono uppercase tracking-widest h-11 px-6">
                    <Package className="h-4 w-4 mr-2" /> Bundle · album + hoodie $50 <span className="ml-2 text-[10px] bg-[#FF4500] text-black px-1.5 py-0.5">save $10</span>
                  </Button>
                </>
              )}
              <Link to={`/merch?album=${album.id}`}>
                <Button data-testid="merch-for-album" variant="outline" className="rounded-none border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono uppercase tracking-widest h-11 px-6">
                  <ShoppingBag className="h-4 w-4 mr-2" /> Merch
                </Button>
              </Link>
            </div>
            {!album.is_free && (
              <div data-testid="album-singles-hint" className="mt-3 font-mono text-xs text-[#FF4500]/90">
                or Grab some singles!! $0.99
              </div>
            )}
            <div className="mt-4">
              <TipJarModal artistId={album.artist_id} artistName={album.artist_name} />
            </div>
            <div className="mt-6">
              <ShareButtons
                kind="album"
                subject={{ title: album.title, artist_name: album.artist_name }}
                preview={{ kind: "album", id: album.id }}
              />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-4">{tracks.length} tracks · MP3 320kbps</div>
          </div>
        </div>

        <TrackList tracks={tracks} contextList={tracks} />
      </div>
    </div>
  );
}
